import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageSquare, X, Send, Loader2 } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_STORAGE_KEY = "pgb_chat_session";

function getSessionId() {
  if (typeof window === "undefined") return "pgb-ssr";
  let sid = window.localStorage.getItem(SESSION_STORAGE_KEY);
  if (!sid) {
    sid = `pgb-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    window.localStorage.setItem(SESSION_STORAGE_KEY, sid);
  }
  return sid;
}

export default function ChatWidget() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(getSessionId);
  const scrollerRef = useRef(null);
  const inputRef = useRef(null);

  // Seed welcome message whenever the chat opens for the first time or when language changes with no msgs yet
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: "assistant", content: t.common.chat.welcome }]);
    }
  }, [open, messages.length, t.common.chat.welcome]);

  // Auto-scroll on new messages
  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, sending]);

  // Focus input when opening
  useEffect(() => {
    if (open && inputRef.current) {
      const timer = setTimeout(() => inputRef.current?.focus(), 120);
      return () => clearTimeout(timer);
    }
  }, [open]);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    setSending(true);
    try {
      const res = await axios.post(`${API}/chat`, {
        session_id: sessionId,
        message: text,
        language: lang,
      });
      const reply = res.data?.reply ?? "";
      setMessages((m) => [...m, { role: "assistant", content: reply }]);
    } catch (err) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: t.common.chat.errorReply },
      ]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Floating chat button (left side, so WhatsApp on the right stays unobstructed) */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t.common.chat.openAria}
          data-testid="chat-open-btn"
          className="fixed bottom-6 left-6 z-50 w-14 h-14 bg-[#0B1B29] text-white shadow-[0_18px_40px_-10px_rgba(11,27,41,0.55)] hover:bg-[#1D4ED8] transition-colors flex items-center justify-center"
          style={{ borderRadius: 0 }}
        >
          <MessageSquare size={22} />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className="fixed inset-x-0 bottom-0 z-50 sm:inset-auto sm:bottom-6 sm:left-6 sm:w-[380px] sm:h-[560px] h-[85vh] bg-white border border-[#E2E8F0] shadow-[0_30px_60px_-12px_rgba(11,27,41,0.35)] flex flex-col"
          data-testid="chat-panel"
          role="dialog"
          aria-modal="false"
        >
          {/* Header */}
          <div className="flex items-center gap-3 bg-[#0B1B29] text-white px-4 py-3 border-b border-white/10">
            <div className="bg-white p-1.5">
              <img src="/brand/planet-group-mark.png" alt="Planet Group Baku" className="h-7 w-auto" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold tracking-tight truncate">
                {t.common.chat.title}
              </div>
              <div className="text-[10px] tracking-[0.18em] uppercase text-white/60 truncate">
                {t.common.chat.subtitle}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label={t.common.chat.closeAria}
              data-testid="chat-close-btn"
              className="w-8 h-8 border border-white/20 hover:bg-white hover:text-[#0B1B29] flex items-center justify-center transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          {/* Messages */}
          <div
            ref={scrollerRef}
            className="flex-1 overflow-y-auto px-4 py-4 bg-[#F5F5F7] space-y-3"
            data-testid="chat-messages"
          >
            {messages.map((m, i) => (
              <div
                key={`msg-${i}`}
                data-testid={`chat-msg-${m.role}`}
                className={`max-w-[85%] text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "ml-auto bg-[#0B1B29] text-white px-4 py-2.5"
                    : "mr-auto bg-white border border-[#E2E8F0] text-[#0B1B29] px-4 py-2.5"
                }`}
              >
                {m.content}
              </div>
            ))}
            {sending && (
              <div className="mr-auto bg-white border border-[#E2E8F0] text-[#536878] px-4 py-2.5 text-sm inline-flex items-center gap-2" data-testid="chat-typing">
                <Loader2 size={14} className="animate-spin" /> {t.common.chat.typing}
              </div>
            )}
          </div>

          {/* Input */}
          <form
            onSubmit={send}
            className="flex items-center gap-2 p-3 border-t border-[#E2E8F0] bg-white"
            data-testid="chat-form"
          >
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t.common.chat.placeholder}
              className="flex-1 pgb-input"
              data-testid="chat-input"
              disabled={sending}
              maxLength={1000}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed py-[14px] px-4"
              aria-label={t.common.chat.send}
              data-testid="chat-send-btn"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
